import {
  AnimationEvent,
  CSSProperties,
  FunctionComponent,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { randomBetween, RandomRange } from '../../helpers';
import { BatFigure } from '../BatFigure';
import { BAT_FOLDED_PATH, BAT_VIEW_BOX } from '../batShapes';

import classNames from 'classnames';
import classes from './RoostingBat.module.scss';

const AWAY_DELAY_MS: RandomRange = { min: 20000, max: 70000 };

const HANG_DURATION_MS: RandomRange = { min: 6000, max: 14000 };

const ROOST_LEFT_VW: RandomRange = { min: 8, max: 88 };

const CALM_FLAP_DURATION = 0.22;

const STARTLED_FLAP_DURATION = 0.17;

enum RoostPhase {
  AWAY = 'away',
  ARRIVING = 'arriving',
  HANGING = 'hanging',
  LEAVING = 'leaving',
  FLEEING = 'fleeing',
}

interface RoostSpot {
  left: number;
  side: number;
}

export const RoostingBat: FunctionComponent = () => {
  const [phase, setPhase] = useState(RoostPhase.AWAY);
  const [spot, setSpot] = useState<RoostSpot>({ left: 50, side: 1 });

  useEffect(() => {
    let timer: ReturnType<typeof setTimeout> | undefined;

    if (phase === RoostPhase.AWAY) {
      timer = setTimeout(() => {
        setSpot({ left: randomBetween(ROOST_LEFT_VW), side: Math.random() < 0.5 ? -1 : 1 });
        setPhase(RoostPhase.ARRIVING);
      }, randomBetween(AWAY_DELAY_MS));
    }

    if (phase === RoostPhase.HANGING) {
      timer = setTimeout(() => setPhase(RoostPhase.LEAVING), randomBetween(HANG_DURATION_MS));
    }

    return () => clearTimeout(timer);
  }, [phase]);

  const animationEndHandler = useCallback(
    ({ target, currentTarget }: AnimationEvent<HTMLDivElement>) => {
      if (target !== currentTarget) {
        return;
      }

      setPhase((phasePrev) =>
        phasePrev === RoostPhase.ARRIVING ? RoostPhase.HANGING : RoostPhase.AWAY
      );
    },
    []
  );

  const scareHandler = useCallback(() => setPhase(RoostPhase.FLEEING), []);

  const figure = useMemo(() => {
    if (phase === RoostPhase.HANGING) {
      return (
        <svg className={classes['roosting-bat__folded']} viewBox={BAT_VIEW_BOX}>
          <path d={BAT_FOLDED_PATH} />
        </svg>
      );
    }

    return (
      <BatFigure
        flapDuration={phase === RoostPhase.FLEEING ? STARTLED_FLAP_DURATION : CALM_FLAP_DURATION}
      />
    );
  }, [phase]);

  const { left, side } = spot;

  if (phase === RoostPhase.AWAY) {
    return null;
  }

  return (
    <div
      className={classNames(classes['roosting-bat'], classes[`roosting-bat--${phase}`])}
      style={{ '--roost-left': `${left}vw`, '--roost-side': side } as CSSProperties}
      onAnimationEnd={animationEndHandler}
      {...(phase === RoostPhase.HANGING && { onClick: scareHandler })}
    >
      {figure}
    </div>
  );
};
